import {
  Component,
  ComponentInterface,
  Element,
  Event,
  EventEmitter,
  h,
  Host,
  Listen,
  Prop,
  State,
  Watch,
  Method
} from '@stencil/core';
import { JuiSelectItems } from './jui-select-items';
import { AddTagFn, JuiSelectOrder, SelectOption } from './jui-select.model';
import { isFunction, isPromise } from './jui-select-utils';
import { defaultLabelTemplate, defaultOptionTemplate } from './templates/defaultTemplates';
import { ChipSize, ChipVariant } from '../../jui-chip/jui-chip.model';
import { TranslationUtils } from '../../../shared/TranslationUtils';
import { Subject } from 'rxjs';
// storybook example custom templates
import { customOptionTemplate } from './stories/customTemplates';
import { JuiControlSize, JuiValidationEvents } from '../../../shared/form-controls/jui-form-control.model';
import { IconSize, IconVariant } from '../../jui-icon/jui-icon.model';
import {
  renderFormError,
  renderTooltipOnBottom,
  renderTooltipOnLeft
} from '../../../shared/form-controls/jui-form-control.utils';

@Component({
  tag: 'jui-select',
  styleUrl: 'jui-select.scss',
  shadow: true
})
export class JuiSelect implements ComponentInterface {
  @Element() el: HTMLElement;
  /**
   * Internal prop
   */
  @Prop() sb = false;

  /**
   * Internal prop
   */
  @Prop() sbitems: string = null;

  /**
   * Internal prop
   */
  @Prop() sboptionstemplate: string = null;

  /**
   * The input label
   */
  @Prop() label: string;

  /**
   * Marking the input field as disabled. Defaults to false
   */
  @Prop() disable: boolean;

  /**
   * Marking the input field as readonly. Defaults to false
   */
  @Prop() readonly: boolean;

  /**
   * Marking the input field as required. Defaults to false
   */
  @Prop() required = false;

  /**
   * Optional help string
   */
  @Prop() help: string = null;

  /**
   * Optional tooltip string
   */
  @Prop() tooltip: string = null;

  /**
   * Optionally autofocus the input. Defaults to false
   */
  @Prop() autofocus = false;

  /**
   * The errors for the input
   */
  @Prop() errors: boolean | { [key: string]: string };

  /**
   * The validation messages for the input
   */
  @Prop() messages: string | { [key: string]: string };

  /**
   * The event to trigger validation check
   */
  @Prop() validation: JuiValidationEvents = JuiValidationEvents.onLoad;

  /**
   * Value to hold the current value for the select.
   */
  @Prop({ attribute: 'value', mutable: true }) value: any = '';

  /**
   * The items data
   */
  @Prop() items: any[];

  /**
   * The loading prop
   */
  @Prop() loading = null;

  /**
   *  The option to select multiple, defaults to false
   */
  @Prop() multiple = true;

  /**
   * Set the size of the input. Options are 'md', 'lg' or 'sm'. Defaults to 'md'
   */
  @Prop() size: JuiControlSize = JuiControlSize.md;

  /**
   * The typeahead subject prop
   */
  @Prop() typeahead: Subject<string>;

  /**
   * The typeahead min search term
   */
  @Prop() minsearchterm = 1;

  /**
   * The max amount of selected chips to display. Defaults to 2.
   */
  @Prop() maxselectedchips = 2;

  /**
   * Highlights search term in the filtered list of options
   */
  @Prop() highlight = true;

  /**
   * Removes selected items from the filtered list of options
   */
  @Prop() hideselected = false;

  /**
   * Set the input field to be clearable
   */
  @Prop() clearable = false;

  /**
   * The explicit object property binding for the label
   */
  @Prop() bindlabel: string = null;

  /**
   * The explicit object property binding for the value
   */
  @Prop() bindvalue: string = null;

  /**
   * The placeholder text for the input
   */
  @Prop() placeholder = '';

  /**
   * Optional alphabetical ordering of the options by their display label ('asc' | 'desc').
   * Unset leaves the options in the order they were provided.
   */
  @Prop() order: JuiSelectOrder = null;

  /**
   * The add new tag prop, defaults to false, but can also take an AddTagFn function that can also return a promise
   */
  @Prop() addtag: boolean | AddTagFn = false;

  /**
   * The form name for the file input control.
   */
  @Prop() name = '';

  /**
   * Optional data-qa string
   */
  @Prop() dataqastring: string = null;

  /**
   * The template
   */
  @Prop() optionstemplate: (item: any) => any = null;

  /**
   * Adds ability to select and unselect all currently selected items
   */
  @Prop() selectallenabled = true;

  /**
   * Display the label inline instead of above the select
   */
  @Prop() inlinelabel = false;

  /**
   * Output event emitted when change is fired
   */
  @Event() public juiChange: EventEmitter<any>;

  /**
   * Output event emitted when focus is fired
   */
  @Event() public juiFocus: EventEmitter<string>;

  /**
   * Output event emitted when blur is fired
   */
  @Event() public juiBlur: EventEmitter<string>;

  /**
   * Output event emitted when option is selected
   */
  @Event() public juiItemSelected: EventEmitter<any>;

  /**
   * Output event emitted when option is unselected
   */
  @Event() public juiItemUnselected: EventEmitter<any>;

  /**
   * Output event emitted when option is unselected
   */
  @Event() public juiSelectOpened: EventEmitter<string>;

  /**
   * Output event emitted when new tag is added
   */
  @Event() public juiTagAdded: EventEmitter<any>;

  @State() _items: any[] = [];

  @State() _selectedItems: any[] = [];

  @State() _loading = false;

  @State() _showAddTag = false;

  @State() _addTagFocused = false;

  @State() _showNoItemsFound = false;

  @State() _allItemsSelected = false;

  @State() searchTerm = '';

  @State() selectItems: JuiSelectItems;

  @State() i18nUtils: TranslationUtils;

  @State()
  private touched = false;

  @State()
  private focused = false;

  @State() i18ns: any;

  private errorMessages = null;

  @State()
  private tooltipLeft = false;

  @State()
  private tooltipLeftBottom = false;

  @State()
  private isClearing = false;

  @State()
  private optionsShown = false;

  @State()
  activeItem: SelectOption = null;

  @Watch('optionsShown')
  optionsShownListener(optionsShown: boolean) {
    if (optionsShown) {
      // set first option as focused;
      this.selectItems.setFocus(0);
      this.filterItems();
    }
  }

  @Watch('activeItem')
  activeItemChange(newVal: string, oldVal: string): void {
    if (newVal != oldVal) {
      this.activeItem = this.selectItems.activeItem;
    }
  }

  @Watch('value')
  valueChanged(newValue: unknown): void {
    if (!this.settingValueInternally) {
      this.showValue(newValue);
    }
  }

  @Watch('loading')
  loadingChanged(loading: boolean): void {
    this.setLoading(loading);
  }

  @Watch('items')
  itemsChanged(items: any[]): void {
    this.setItems(items);
    if (!this.typeahead) {
      this.setSelectedValue();
    }
    // Re-render selected items to ensure chips are properly updated
    this.renderSelectedItems();
  }

  @Watch('sbitems')
  sbitemsChanged(sbitems: string): void {
    if (this.sb && sbitems) {
      try {
        const parsedItems = JSON.parse(sbitems);
        if (Array.isArray(parsedItems)) {
          this.setItems(parsedItems);
          if (!this.typeahead) {
            this.setSelectedValue();
          }
          // Re-render selected items to ensure chips are properly updated
          this.renderSelectedItems();
        }
      } catch (e) {
        console.error('Failed to parse sbitems:', e, sbitems);
      }
    }
  }

  @Listen('juiChipRemoved')
  chipRemovedHandler(evt: CustomEvent): void {
    const foundItem = this.selectItems.selectedItems.find(x => {
      // Storybook specific check
      if (this.sb && typeof evt.detail.value === 'string') {
        return x.value == evt.detail.value;
      }
      return JSON.stringify(x.value) === JSON.stringify(evt.detail.value);
    });
    if (foundItem) {
      this.selectItems.unselect(foundItem);
      this.juiItemUnselected.emit(foundItem);
      this.renderSelectedItems();
      this.updateValue();
      this.postSelection();
    }
  }

  @Listen('juiSelectOpened', { target: 'window' })
  handleSelectedOpened(evt) {
    if (evt.detail !== this.optionsContainerId) {
      this.optionsShown = false;
    }
  }

  @Listen('click', { target: 'window' })
  closeOptionsIfOpen(evt): void {
    if (!this.optionsShown) {
      return;
    }
    const clickedOutsideOptionsContainer = !document.querySelector(`#${this.optionsContainerId}`).contains(evt.target);
    if (clickedOutsideOptionsContainer) {
      this.optionsShown = !this.optionsShown;
    }
  }

  @Listen('keydown')
  async keydownHandler(evt: KeyboardEvent): Promise<void> {
    // adds a11y keyboard controls

    // todo we want to open options when searching
    // open and close options list
    if (evt.key === ' ') {
      if (this.searchTerm && !this.searchTerm.trim().length && !this.optionsShown) {
        this.searchTerm = '';
      }

      // handle open
      if (!this.optionsShown) {
        this.optionsShown = !this.optionsShown;
      }
    }

    if (evt.key === 'Escape') {
      if (this.optionsShown) {
        this.optionsShown = !this.optionsShown;
      }
      if (this._showNoItemsFound) {
        this._showNoItemsFound = false;
      }
      this.selectItems.setActiveItem();
      this.clearSearchTerm();
      this.renderSelectedItems();
      return;
    }

    // up and down the list
    if (evt.key === 'ArrowDown') {
      if (!this.optionsShown) {
        this.optionsShown = !this.optionsShown;
      } else {
        if (
          this._showAddTag &&
          this.selectItems.getCurrentFocusIndex() === this.selectItems.filteredItems.length - 1 &&
          !this._addTagFocused
        ) {
          // check if we are the last element in the list then focus add tag
          this._addTagFocused = true;
          this.selectItems.unsetActiveItem();
        } else {
          this._addTagFocused = false;
          this.selectItems.setNextFocused(this.searchTerm);
        }
      }
      this.activeItem = this.selectItems.activeItem;
      this.scrollToActiveElement(evt.key);
      evt.preventDefault();
      return;
    }

    if (evt.key === 'ArrowUp') {
      if (!this.optionsShown) {
        return;
      }
      if (this._showAddTag && this.selectItems.getCurrentFocusIndex() === 0 && !this._addTagFocused) {
        // check if we are the last element in the list then focus add tag
        this._addTagFocused = true;
        this.selectItems.unsetActiveItem();
      } else {
        this._addTagFocused = false;
        this.selectItems.setPrevFocused(this.searchTerm);
      }
      this.activeItem = this.selectItems.activeItem;
      this.scrollToActiveElement(evt.key);
      evt.preventDefault();
      return;
    }

    if (evt.key === 'Enter') {
      if (!this.optionsShown) {
        return;
      }
      if (this._addTagFocused) {
        this.selectAndCreateNewTag();
        return;
      }
      if (this.activeItem) {
        this.selectItem(this.activeItem);
        this.activeItem = this.selectItems.activeItem;
        return;
      }
      return;
    }

    if (evt.key === 'Tab' || evt.key === 'Shift') {
      // respect power user navigate to and from control
      if (this.optionsShown) {
        this.optionsShown = false;
      }

      return;
    }

    // Open the options if the user inputs a valid search character
    const validInputRegex = new RegExp(/^[a-zA-Z0-9\s-]*$/);
    if (!this.optionsShown && validInputRegex.test(evt.key)) {
      this.optionsShown = !this.optionsShown;
    }
  }

  private readonly _defaultLabel = 'label';
  private readonly juiSelectOptionsStyleId = 'juiSelectOptionsStyles';
  private optTemplate;
  private labelTemplate;
  private optionsContainerId;
  private settingValueInternally = false;

  constructor() {
    this.selectItems = new JuiSelectItems(this);
  }

  private getOptionHeight(): number {
    switch (this.size) {
      case JuiControlSize.lg:
        return 49;
      case JuiControlSize.md:
        return 45;
      case JuiControlSize.sm:
        return 42;
    }
  }

  private scrollToActiveElement(keyEvent: string): void {
    const activeItemElement = document.querySelector(
      `#${this.optionsContainerId} .jui-select__option--focused`
    ) as HTMLElement;
    const scrollElement = document.querySelector(`#${this.optionsContainerId} .jui-select__options-scroll`);

    if (!activeItemElement || !scrollElement) {
      return;
    }
    const optionHeight = this.getOptionHeight();

    if (keyEvent === 'ArrowDown') {
      const needsToScroll =
        activeItemElement.offsetTop + optionHeight * 2 >= scrollElement.scrollTop + scrollElement.clientHeight;

      if (!needsToScroll) {
        return;
      }

      if (
        activeItemElement.offsetTop < scrollElement.clientHeight - 2 * optionHeight ||
        activeItemElement.offsetTop >= scrollElement.scrollHeight - optionHeight
      ) {
        scrollElement.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        scrollElement.scrollTo({ top: activeItemElement.offsetTop - 2 * optionHeight, behavior: 'smooth' });
      }
    }

    if (keyEvent === 'ArrowUp') {
      const needsToScroll =
        activeItemElement.offsetTop + optionHeight * 4 <= scrollElement.clientHeight + scrollElement.scrollTop;

      if (!needsToScroll) {
        return;
      }

      if (activeItemElement.offsetTop < optionHeight) {
        scrollElement.scrollTo({ top: scrollElement.scrollHeight - 2 * optionHeight, behavior: 'smooth' });
      } else {
        scrollElement.scrollTo({ top: activeItemElement.offsetTop - optionHeight, behavior: 'smooth' });
      }
    }
  }

  private initStorybookProps(): void {
    // we have to create some storybook specific props prefixed with sb.
    // This is to mimic the input of complex data types as
    // currently there is no nice way to pass in via templated strings.
    this.initTranslations();
    this.labelTemplate = defaultLabelTemplate;
    this.setLoading(this.loading);
    if (this.value) {
      this.setValueInternally(JSON.parse(this.value));
    }
    if (this.sbitems) {
      this.setItems(JSON.parse(this.sbitems));
      this.renderSelectedItems();
      this.setSelectedValue();
    }

    // todo may want to showcase custom label template too
    if (this.sboptionstemplate) {
      switch (this.sboptionstemplate) {
        case 'custom':
          this.optTemplate = customOptionTemplate;
          break;
        default:
          this.optTemplate = defaultOptionTemplate;
          break;
      }
    }
  }

  handleSelectAll(evt) {
    this.toggleSelectAll(!!evt.detail);
  }

  private toggleSelectAll(selectAll: boolean): void {
    if (selectAll) {
      this.selectItems.selectAll();
      this._allItemsSelected = true;
      this.renderSelectedItems();
      this.updateValue();
      this.postSelection();
    } else {
      this.unselectAllItems();
    }
  }

  handleFocusFromHover(item: SelectOption): void {
    this.selectItems.setFocusFromItem(item);
    if (!this.typeahead) {
      this.filterItems();
    }
    this.activeItem = this.selectItems.activeItem;
  }

  private handleOptionClick(item: SelectOption): void {
    this.selectItem(item);
  }

  private selectItem(item: SelectOption): void {
    if (item.selected) {
      this.selectItems.unselect(item);
      this.juiItemUnselected.emit(item.value);
    } else {
      this.juiItemSelected.emit(item.value);
      this.selectItems.select(item);
    }
    this.updateValue();
    this.postSelection();
  }

  private detectSelectAll() {
    this._allItemsSelected = this.selectItems.items.every(item => item.selected);
  }

  private postSelection(): void {
    if (!this.multiple) {
      this.clearSearchTerm();
    }
    this.filterItems();
    this.focusControl();
    this.renderSelectedItems();
    this.detectSelectAll();
    if (!this.multiple && this._selectedItems?.length) {
      this.optionsShown = false;
    }
  }

  private handleSearch(e): void {
    this.searchTerm = (e.target as HTMLInputElement).value;
    if (!this.multiple) {
      this.renderSelectedItems();
    }
    if (this.typeahead && this.searchTerm.length >= this.minsearchterm) {
      this._showAddTag = false;
      this._addTagFocused = false;
      this.typeahead.next(this.searchTerm);
    } else {
      // filter items
      this.filterItems();
      this._addTagFocused = false;
      if (!this.selectItems.filteredItems.length && this._showAddTag) {
        this._addTagFocused = true;
      } else {
        this.selectItems.setFocus(0);
      }
      this.activeItem = this.selectItems.activeItem;
    }
  }

  private unselectAllItems(): void {
    this.selectItems.clearSelectedItems();
    this._allItemsSelected = false;
    this.selectItems.mapSelectedItemValues();
    this.renderSelectedItems();
    this.updateValue();
    this.postSelection();
  }

  private getSelectedFilteredItems() {
    return this.selectItems.filteredItems?.filter(item => {
      return item.selected;
    });
  }

  private getUnselectedFilteredItems() {
    return this.selectItems.filteredItems?.filter(item => {
      if (!this.multiple) return item;
      return !item.selected;
    });
  }

  private hideFilteredItems() {
    if (this._showNoItemsFound || this._showAddTag) {
      return false;
    }
    return !this.optionsShown || !this.getUnselectedFilteredItems().length;
  }

  private hideSelectedItems() {
    return !this.optionsShown || !this.selectItems.selectedItems.length;
  }

  private toggleOptions(evt): void {
    if (this.disable || this.readonly) {
      return;
    }
    evt.stopPropagation();
    this.juiSelectOpened.emit(this.optionsContainerId);
    this.optionsShown = !this.optionsShown;
    this.focusControl();
    this.activeItem = this.selectItems.activeItem;
  }

  private toggleOptionsFromClick(evt): void {
    if (this.disable || this.readonly) {
      return;
    }
    evt.stopPropagation();
    this.juiSelectOpened.emit(this.optionsContainerId);
    this.optionsShown = false;
    if (!this.optionsShown) {
      this.optionsShown = !this.optionsShown;
    }
    this.focusControl();
    this.activeItem = this.selectItems.activeItem;
  }

  private get _validSearchTerm() {
    const term = this.searchTerm && this.searchTerm.trim();
    return term && term.length >= this.minsearchterm;
  }

  componentWillUpdate(): void {
    this.renderError();
  }

  generateRandomId(): string {
    if (window && window.self?.crypto?.randomUUID) {
      return window.self.crypto.randomUUID();
    } else {
      return 'random_hash';
    }
  }

  componentWillLoad(): Promise<void> | void {
    const uuid = this.generateRandomId();
    this.optionsContainerId = `options-container--${uuid}`;

    if (this.sb) {
      this.initStorybookProps();
    } else {
      this.init();
    }
    if (this.validation === JuiValidationEvents.onLoad) {
      this.touched = true;
      this.renderError();
    }
  }

  private filterItems(): void {
    this.selectItems.filterItems(this.searchTerm);
    if (this.addtag) {
      this._showAddTag = this.shouldShowAddTag();
    }
    this._showNoItemsFound = !this.selectItems.filteredItems.length;
    this.activeItem = this.selectItems.activeItem;
  }

  private shouldShowAddTag(): boolean {
    if (!this._validSearchTerm) {
      return false;
    }
    const term = this.searchTerm.toLowerCase().trim();

    return (
      this.addtag &&
      !this.selectItems.filteredItems.some(x => x.label.toLowerCase() === term) &&
      !this.selectItems.selectedItems.some(x => x.label.toLowerCase() === term) &&
      !this._loading
    );
  }

  private selectAndCreateNewTag() {
    if (this._loading) {
      return;
    }
    let tag;
    if (isFunction(this.addtag)) {
      tag = (this.addtag as AddTagFn)(this.searchTerm);
    } else {
      tag = !this.bindlabel ? this.searchTerm : { [this.bindlabel]: this.searchTerm };
    }

    if (isPromise(tag)) {
      this.setLoading(true);
      tag
        .then(resolvedTag => {
          this.selectItem(this.selectItems.parseAndAddItem(resolvedTag));
          this.resetSearch();
          this._addTagFocused = false;
          this.juiTagAdded.emit(resolvedTag);
        })
        .catch(() => {
          this.setLoading(false);
        });
    } else if (tag) {
      this.selectItem(this.selectItems.parseAndAddItem(tag));
      this.resetSearch();
      this._addTagFocused = false;
      this.juiTagAdded.emit(tag);
    }
  }

  private resetSearch(): void {
    this.clearSearchTerm();
    if (this.typeahead) {
      this.selectItems.setItems([]);
    }
    this.filterItems();
    this.setLoading(false);
  }

  private clearSearchTerm(): void {
    this.searchTerm = null;
  }

  private setLoading(loading: boolean): void {
    this._loading = loading;
  }

  private setSelectedValue(): void {
    if (!this.value) {
      return;
    }
    // always set as array
    if (!Array.isArray(this.value)) {
      this.setValueInternally([this.value]);
    }

    // we should set the initial select items from the value
    // if we use async options (typeahead or async items)
    if (!this.selectItems.items.length) {
      this.selectItems.setItems(this.value);
    }

    this.value.forEach(val => {
      const found = this.selectItems.findItemByValue(val);
      if (found) {
        this.selectItems.select(found);
      }
    });

    // this.updateValue(); // todo - causes juiChange to fire twice when selecting
    this.renderSelectedItems();
  }

  private updateValue(): void {
    this.setValueInternally(this.selectItems.selectedValue);
    this.juiChange.emit(this.value);
  }

  private setTypeaheadFocus(): void {
    if (!this.typeahead) {
      return;
    }
    if (this.selectItems.filteredItems && this.selectItems.filteredItems.length) {
      this.selectItems.setFocus(0);
      this.activeItem = this.selectItems.activeItem;
    } else if (this.shouldShowAddTag()) {
      this._addTagFocused = true;
    }
  }

  private setItems(items: any[]): void {
    if (items && items.length) {
      this.bindlabel = this.bindlabel || this._defaultLabel;
      this.selectItems.setItems(items);
    }
    this.filterItems();
    this.setTypeaheadFocus();
  }

  private focusControl(): void {
    const inputEl = this.el.shadowRoot.querySelector('.jui-select__combo-input') as HTMLInputElement;
    inputEl.focus();
  }

  private onFocus(event: Event): void {
    this.touched = true;
    this.focused = true;
    this.setValueInternally((event.target as HTMLInputElement).value);
    this.juiFocus.emit(this.value);
  }

  private onBlur(event: Event): void {
    this.setValueInternally((event.target as HTMLInputElement).value);
    this.focused = false;
    this.juiBlur.emit(this.value);
  }

  private initTranslations(): void {
    this.i18nUtils = TranslationUtils.instance;
    this.i18ns = {};
    this.i18ns.createNew = this.i18nUtils.translate('components.juiSelect.createNew', 'Create new');
    this.i18ns.loading = this.i18nUtils.translate('components.juiSelect.loading', 'Loading');
    this.i18ns.noItemsFound = this.i18nUtils.translate('components.juiSelect.noItemsFound', 'No items found');
    this.i18ns.selectAll = this.i18nUtils.translate('components.juiSelect.selectAll', 'Select all');
  }

  init(): void {
    this.initTranslations();
    this.optTemplate = defaultOptionTemplate;
    this.labelTemplate = defaultLabelTemplate;

    this.setLoading(this.loading);
    this.renderSelectedItems();
    this.setItems(this.items);
    this.setSelectedValue();

    if (this.optionstemplate) {
      this.optTemplate = this.optionstemplate;
    }
  }

  /**
   * Public method for setting value from external sources (e.g., Angular form patchValue)
   * This bypasses the internal value update flow and directly updates the selection state
   */
  @Method()
  async setValueFromForm(newValue: any): Promise<void> {
    this.showValue(newValue);
  }

  private setValueInternally(value: unknown): void {
    this.settingValueInternally = true;
    this.value = value;
    this.settingValueInternally = false;
  }

  private showValue(newValue: unknown): void {
    // Only process if component is initialized
    if (!this.labelTemplate) {
      return;
    }

    // Handle clearing of value
    if (!newValue || (Array.isArray(newValue) && newValue.length === 0)) {
      this.selectItems.clearSelectedItems();
      this.renderSelectedItems();
      return;
    }

    // Ensure value is always an array for processing
    const newValueArray = Array.isArray(newValue) ? newValue : [newValue];

    // With async options (typeahead or items still to come) the value seeds the items, as on load
    if (!this.selectItems.items.length) {
      this.selectItems.setItems(newValueArray);
    }

    // Clear previous selections
    this.selectItems.clearSelectedItems();

    // Set new selected values
    newValueArray.forEach(val => {
      const found = this.selectItems.findItemByValue(val);
      if (found) {
        this.selectItems.select(found);
      }
    });

    // Update the UI
    this.renderSelectedItems();
  }

  getRenderedContainerHeight() {
    const selectAllSpace = this.selectallenabled && this.multiple ? 45 : 0;
    const totalOptionHeight = this.selectItems.filteredItems.length * this.getOptionHeight();
    const maxHeight = 290;
    return Math.min(totalOptionHeight + selectAllSpace, maxHeight);
  }

  getContainerPositioning() {
    const comboEl = this.el.shadowRoot.querySelector('.jui-select__combo') as HTMLElement;
    const rect = comboEl.getBoundingClientRect();
    const distanceFromBottomWindow = window.innerHeight - (rect.top + comboEl.offsetHeight);
    const requiredSpace = this.getRenderedContainerHeight();
    const width = comboEl.clientWidth + 2;

    if (distanceFromBottomWindow < requiredSpace) {
      return {
        left: rect.left + window.scrollX,
        bottom: distanceFromBottomWindow - window.scrollY + comboEl.offsetHeight,
        width
      };
    }
    return {
      left: rect.left + window.scrollX,
      top: rect.top + window.scrollY + comboEl.clientHeight,
      width
    };
  }

  getContainerElement() {
    return document.querySelector(`#${this.optionsContainerId}`);
  }

  disconnectedCallback() {
    // remove container from the dom
    const container = document.querySelector(`#${this.optionsContainerId}`);
    if (container) {
      container.remove();
    }
  }

  private repositionDropdown() {
    const positioning = this.getContainerPositioning();
    const optionsContainerElement = document.querySelector(`#${this.optionsContainerId}`) as HTMLElement;

    if (!optionsContainerElement) {
      return;
    }

    if (positioning.bottom) {
      optionsContainerElement.style.bottom = `${positioning.bottom}px`;
      optionsContainerElement.style.top = 'unset';
    } else {
      optionsContainerElement.style.top = `${positioning.top}px`;
      optionsContainerElement.style.bottom = 'unset';
    }
    optionsContainerElement.style.width = `${positioning.width}px`;
    optionsContainerElement.style.left = `${positioning.left}px`;
  }

  private appendOptionsStylesToHead() {
    // check to see if this is already included by another select
    if (document.head.querySelector(`#${this.juiSelectOptionsStyleId}`)) {
      return;
    }
    // we append styles to the head for this component because we append options to the body
    // so that we can ensure options are not hidden behind any overflows.
    const styleEl = document.createElement('style');
    styleEl.id = this.juiSelectOptionsStyleId;
    styleEl.textContent = `
        .jui-select__options-container {
           box-sizing: border-box;
           background-color: var(--jds-theme-color-input-bg, #fff);
           font-family: var(--jds-theme-ff, sans-serif);
           font-size: var(--jds-theme-input-fs, 14px);
           z-index: 1050;
           overflow: hidden;
           max-height: 290px;
           box-shadow: 0 1px 0 rgba(0, 0, 0, 0.06);
           border: 1px solid var(--jds-theme-color-input-border, var(--jds-base-grey-400));
           position: absolute;
        }
        .jui-select__options-scroll {
          position: relative;
          overflow-y: auto;
          max-height: 246px;
        }
        .jui-select__options-container--hidden {
           box-shadow: none;
           border: none;
           display:none;
        }
        .jui-select__all {
           z-index: 1;
           background: var(--jds-theme-color-input-bg-readonly, var(--jds-base-white-300));
           padding: var(--jds-space-inset);
        }
        .jui-select__options {
           position: relative;
         }
        .jui-select__options--hidden {
           display: none;
        }
        .jui-select__option--hidden {
           display: none;
        }
        .jui-select__option {
           color: var(--jds-theme-color-input-text);
           box-sizing: border-box;
           cursor: pointer;
           white-space: nowrap;
           overflow: hidden;
           text-overflow: ellipsis;
           padding: var(--jds-space-inset);
        }
        .jui-select__option--loading {
           font-weight: 600;
           color: var(--jds-theme-color-primary);
        }

        .jui-select__option--focused {
           background-color: var(--jds-theme-color-option-bg-hover, var(--jds-base-white-300));
        }

        .jui-select__option--selected {
           font-weight: 600;
           background-color: var(--jds-theme-color-option-bg-selected, var(--jds-base-white-500));
        }

       .jui-select__no-items {
           padding: var(--jds-space-inset);
           font-weight: 600;
           font-style: italic;
        }

       .jui-select__create-new {
           cursor: pointer;
           font-weight: 600;
           color: var(--jds-theme-color-primary);
       }

       .jui-select__highlighted {
           text-decoration: underline;
           font-weight: 600;
           color: var(--jds-theme-color-primary);
       }


       .jui-select__options-container--lg {
           font-size: var(--jds-theme-input-fs-lg, 16px);
       }

       .jui-select__options-container--sm {
           font-size: var(--jds-theme-input-fs-sm, 12px);
       }
    `;
    document.head.append(styleEl);
  }

  private appendOptionsToBody() {
    const optionsContainerElement = this.el.shadowRoot.querySelector(`#${this.optionsContainerId}`) as HTMLElement;
    document.body.append(optionsContainerElement);
  }

  componentDidLoad(): void {
    if (this.autofocus) {
      this.focusControl();
    }

    this.appendOptionsStylesToHead();
    this.appendOptionsToBody();
    this.repositionDropdown();
  }

  componentDidRender(): void {
    if (this.tooltip) {
      this.renderTooltips();
    }
    if (this.optionsShown) {
      this.repositionDropdown();
    }
  }

  private renderSelectedItems(): void {
    const limitShownChips = !this.hideselected && !this.typeahead;
    const moreSelectedItemsThanMaxAmount =
      limitShownChips && this.selectItems.selectedItems?.length > this.maxselectedchips;

    moreSelectedItemsThanMaxAmount
      ? (this._selectedItems = this.selectItems.selectedItems?.slice(0, this.maxselectedchips).map(item => {
          return [
            <jui-chip
              tabindex={this.disable ? -1 : 1}
              value={item.value}
              key={item.value}
              size={this.size === JuiControlSize.lg ? ChipSize.md : ChipSize.sm}
              color={ChipVariant.mid}
              removable={!this.disable && !this.readonly}
            >
              {this.labelTemplate(item)}
            </jui-chip>,
            null
          ];
        }))
      : (this._selectedItems = this.selectItems.selectedItems?.map(item => {
          return [
            <jui-chip
              tabindex={this.disable ? -1 : 1}
              value={item.value}
              key={item.value}
              size={this.size === JuiControlSize.lg ? ChipSize.md : ChipSize.sm}
              color={ChipVariant.mid}
              removable={!this.disable && !this.readonly}
            >
              {this.labelTemplate(item)}
            </jui-chip>,
            null
          ];
        }));
    if (moreSelectedItemsThanMaxAmount) {
      const moreItemsSelectedNum = this.selectItems.selectedItems?.length - this.maxselectedchips;
      this._selectedItems.push([
        <jui-chip
          tabindex={this.disable ? -1 : 1}
          value="more"
          key="moreItems"
          size={this.size === JuiControlSize.lg ? ChipSize.md : ChipSize.sm}
          color={ChipVariant.mid}
        >
          {moreItemsSelectedNum} more selected
        </jui-chip>,
        null
      ]);
    }
    if (!this.multiple) {
      this._selectedItems = this.selectItems.selectedItems.length
        ? this.selectItems.selectedItems?.map(item => {
            return (
              <div
                tabindex={this.disable ? -1 : 1}
                data-testid="option-single-selected"
                class={{
                  'jui-select__single-item': true,
                  'jui-select__single-item--hidden': !!this.searchTerm && !this.multiple
                }}
              >
                {this.labelTemplate(item)}
              </div>
            );
          })
        : null;
    }
  }

  render() {
    const errorFragment = this.errorMessages ? this.errorMessages : null;
    const placeholder = this.placeholder.length ? this.placeholder : this.label;
    const dataQaTagLabel = this.dataqastring ? `${this.dataqastring}_formFieldLabel` : null;
    const dataQaTagInput = this.dataqastring ? `${this.dataqastring}_formField` : null;
    const dataQaTagOptionsContainer = this.dataqastring ? `${this.dataqastring}_optionsContainer` : null;
    const dataQaTagSelectAllOption = this.dataqastring ? `${this.dataqastring}_selectAllOption` : null;

    const arrowName = this.optionsShown ? 'arrow_drop_up' : 'arrow_drop_down';

    const helpText = this.help ? <div class="jui-control__help">{this.help}</div> : null;

    const tooltip = this.tooltip ? (
      <div
        class={{
          'jui-control__tooltip': true,
          [`jui-control__tooltip--input-${this.size}`]: true
        }}
      >
        <jui-icon
          class={{ 'jui-control__tooltip-icon': true }}
          name="help_outline"
          cursor="help"
          color={IconVariant.info}
          size={IconSize.xs}
          tabindex={-1}
        ></jui-icon>
        <span
          class={{
            'jui-control__tooltip-text': true,
            'jui-control__tooltip-text--left': this.tooltipLeft && !this.tooltipLeftBottom,
            'jui-control__tooltip-text--left-bottom': this.tooltipLeftBottom
          }}
        >
          {this.tooltip}
        </span>
      </div>
    ) : null;

    return (
      <Host data-qa={this.dataqastring || null}>
        <div
          class={{
            'jui-control': true,
            [`jui-control--${this.size}`]: true
          }}
          data-containerId={this.optionsContainerId}
        >
        {this.label && !this.inlinelabel ? (
          <label
            {...(dataQaTagLabel && { 'data-qa': dataQaTagLabel })}
            data-testid="control-label"
            class={{
              'jui-control__label': true,
              'jui-control__label--has-focus': !!this.focused,
              'jui-control__label--has-value': !!this.value,
              'jui-control__label--disabled': !!this.disable,
              'jui-control__label--narrower': !!this.tooltip
            }}
          >
            <div
              class={{
                'jui-control__label-text': true
              }}
            >
              {this.required ? `${this.label} *` : this.label}
            </div>
          </label>
        ) : null}
        {helpText}
        {tooltip}
        <div class="jui-select">
          <div class="jui-select__container">
            {this.label && this.inlinelabel ? (
              <div class="jui-select__inline-label" {...(dataQaTagLabel && { 'data-qa': dataQaTagLabel })}>
                {this.label}
              </div>
            ) : null}
            {/* onclick to open options add CSS modifiers to __combo */}
            <div
              data-testid="combo"
              class={{
                'jui-select__combo': true,
                'jui-select__combo--invalid': (!!this.errors && this.touched) || (!!this.errors && this.focused),
                'jui-select__combo--has-focus': !!this.isClearing || !!this.focused,
                'jui-select__combo--has-options': this.optionsShown,
                'jui-select__combo--disabled': this.disable || this.readonly,
                'jui-select__combo--inline-label': this.inlinelabel
              }}
              onClick={evt => this.toggleOptionsFromClick(evt)}
            >
              <div
                class="jui-select__combo-control"
                data-testid="combo-control"
                {...(dataQaTagInput && { 'data-qa': dataQaTagInput })}
                data-id={this.optionsContainerId}
              >
                {this._loading && !this._selectedItems?.length ? (
                  <span>{this.i18ns.loading}...</span>
                ) : (
                  this._selectedItems
                )}

                <div
                  class={{
                    'jui-select__combo-box': true,
                    'jui-select__combo-box--single': !this.multiple,
                    'jui-select__combo-box--readonly': this.readonly
                  }}
                >
                  <input
                    data-testid="combo-input"
                    class="jui-select__combo-input"
                    name={this.name}
                    type="text"
                    role="combobox"
                    aria-controls={this.optionsContainerId}
                    aria-autocomplete="list"
                    aria-expanded="false"
                    autocomplete="off"
                    disabled={this.disable}
                    placeholder={!this._selectedItems?.length && !this._loading ? placeholder : ''}
                    value={this.searchTerm}
                    onInput={evt => this.handleSearch(evt)}
                    onFocusin={this.onFocus.bind(this)}
                    onFocusout={this.onBlur.bind(this)}
                  />
                </div>
              </div>

              <div class="jui-select__combo-icons">
                {(this.clearable || this.multiple) && this._selectedItems?.length && !this.disable && !this.readonly ? (
                  <div class="jui-select__combo-icon">
                    <jui-icon
                      data-testid="combo-clear"
                      name="clear"
                      size={IconSize.xs}
                      color={IconVariant.high}
                      onClick={() => this.unselectAllItems()}
                    ></jui-icon>
                  </div>
                ) : null}
                <div class="jui-select__combo-icon">
                  <jui-icon
                    tabindex={-1}
                    name={arrowName}
                    size={IconSize.xs}
                    color={IconVariant.high}
                    onClick={evt => this.toggleOptions(evt)}
                  ></jui-icon>
                </div>
              </div>
            </div>
          </div>

          {/* options list*/}
          <div
            data-testid="options-container"
            data-qa={dataQaTagOptionsContainer}
            class={{
              'jui-select__options-container': true,
              [`jui-select__options-container--${this.size}`]: true,
              'jui-select__options-container--hidden': !this.optionsShown
            }}
            id={this.optionsContainerId}
            role="listbox"
          >
            {/* The combobox options */}
            {this.selectallenabled && this.multiple && this.optionsShown && !this.typeahead ? (
              <div
                role="option"
                tabindex={1}
                class={{
                  'jui-select__all': true
                }}
                onClick={evt => {
                  // The checkbox is disabled while loading, but the row around it still receives
                  // clicks, so it needs the same guard.
                  if (this._loading) {
                    return;
                  }
                  // Clicking the checkbox itself already emits onJuiChecked -> handleSelectAll;
                  // ignore it here so the row click doesn't double-toggle and cancel out.
                  if ((evt.target as HTMLElement).closest('jui-checkbox')) {
                    return;
                  }
                  this.toggleSelectAll(!this._allItemsSelected);
                }}
              >
                <jui-checkbox
                  data-testid="options-select-all"
                  data-qa={dataQaTagSelectAllOption}
                  disable={this._loading}
                  label={this.i18ns.selectAll}
                  checked={this._allItemsSelected}
                  emiteventonload={false}
                  onJuiChecked={evt => this.handleSelectAll(evt)}
                ></jui-checkbox>
              </div>
            ) : null}

            <div class="jui-select__options-scroll">
              {this._loading ? (
                <div class="jui-select__option jui-select__option--loading">{this.i18ns.loading}...</div>
              ) : null}

              {!this._loading && !this.hideselected && this.multiple ? (
                <div
                  class={{
                    'jui-select__options': true,
                    'jui-select__options--hidden': !!this.hideSelectedItems()
                  }}
                  role="group"
                >
                  {this.getSelectedFilteredItems()?.map(item => {
                    const dataQaStringForSelectedOption = `${this.dataqastring}_selected_option--${item.label}`;
                    return (
                      <div
                        data-testid="selected-option"
                        data-qa={dataQaStringForSelectedOption}
                        role="option"
                        class={{
                          'jui-select__option--selected': !!item.selected,
                          'jui-select__option--focused': !!item.focused && !!this.activeItem,
                          'jui-select__option--hidden': !!this.hideSelectedItems(),
                          'jui-select__option': true
                        }}
                        data-active-item={!!item.focused}
                        key={item.label}
                        onClick={() => this.handleOptionClick(item)}
                        onMouseEnter={() => this.handleFocusFromHover(item)}
                      >
                        {this.optTemplate(item, this.highlight, this.searchTerm)}
                      </div>
                    );
                  })}
                </div>
              ) : null}

              {!this._loading
                ? this.getUnselectedFilteredItems().map(item => {
                  const dataQaStringForFilteredOption = `${this.dataqastring}_filtered_option--${item.label}`;
                    return (
                      <div
                        data-testid="filtered-option"
                        data-qa={dataQaStringForFilteredOption}
                        role="option"
                        class={{
                          'jui-select__option': true,
                          'jui-select__option--selected': !!item.selected,
                          'jui-select__option--focused': !!item.focused && !!this.activeItem,
                          'jui-select__option--hidden': !!this.hideFilteredItems()
                        }}
                        data-active-item={!!item.focused}
                        key={item.label}
                        onClick={() => this.handleOptionClick(item)}
                        onMouseEnter={() => this.handleFocusFromHover(item)}
                      >
                        {this.optTemplate(item, this.highlight, this.searchTerm)}
                      </div>
                    );
                  })
                : null}

              {this._showNoItemsFound && !this._showAddTag && !this._loading ? (
                <div class="jui-select__no-items">{this.i18ns.noItemsFound}</div>
              ) : null}
              {/* The create new tag action */}
              {this._showAddTag ? (
                <div
                  data-testid="options-create-new"
                  class={{
                    'jui-select__option': true,
                    'jui-select__option--focused': !!this._addTagFocused
                  }}
                  onClick={this.selectAndCreateNewTag.bind(this)}
                >
                  {this.i18ns.createNew}: <span class="jui-select__create-new">{this.searchTerm}</span>
                </div>
              ) : null}
            </div>
          </div>
        </div>
        {errorFragment}
        </div>
      </Host>
    );
  }

  private renderTooltips(): void {
    this.tooltipLeft = renderTooltipOnLeft(this.el, 'jui-select__combo');
    if (this.tooltipLeft) {
      this.tooltipLeftBottom = renderTooltipOnBottom(this.el, 'jui-select__combo');
    }
  }

  private renderError() {
    this.errorMessages = renderFormError(this.errors, this.messages);
  }
}
